/**
 * Волна C (docs/MIGRATION_ROUTER.md §5). Было: <PrivateRoute path='/promotions/:id'>.
 */
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import CacheDataRoute from '../../CacheDataRoute';
import Promotion from '@routes/Promotion';
import { getQueryClient } from '../../providers/query';
import {
  buildPromotionByIdKeyParams,
  parsePromotionUrlData,
  promotionByIdInfiniteQueryOptions
} from '@queries/index';
import { isContentNotFoundError } from '../../api/contentNotFound';

/**
 * ЭТАП 3 п.3 — КАНОНИЗАЦИЯ АДРЕСА АКЦИИ (301), симметрично рецептам.
 *
 * LazarServer (`lib/routes/promotions.ts`) держал у акции один адрес —
 * `slug-id`: голый `/promotions/353` уходил 301 на
 * `/promotions/festival-kraba-353`, чужой slug — 301 на `/promotions`.
 * У нас чужой slug даёт 200: бэк ищет СНАЧАЛА по id и только потом по
 * identify (`promotionsResolver.findPromotionByIdTemp` — «приоритет поиска
 * по id»), поэтому `/promotions/абракадабра-353` отдавал ту же страницу.
 * Разбор — в `docs/SSR_LAZARSERVER_AUDIT.md`, п.3.
 *
 * Данные берём из уже загруженного ответа (его ждёт честный 404) — лишнего
 * запроса нет. Запрос бесконечный, поэтому в кэше лежит `{ pages: [...] }`,
 * и акция — в первой странице.
 *
 * ГРАБЛИ (поймано прогоном 29.08.2026): страницы в кэше — это НЕ сырой ответ
 * GraphQL. `queryFn` у `promotionByIdInfiniteQueryOptions` разворачивает его
 * сам (`.then(res => res.findPromotionByIdTemp)`), поэтому путь именно
 * `pages[0].promotion`, а не `pages[0].findPromotionByIdTemp.promotion`.
 * С лишним уровнем чтение просто отдавало undefined, и канонизация молча не
 * срабатывала — 200 вместо 301. У рецепта наоборот: там `queryFn` отдаёт
 * ответ целиком (разворачивает `select`, а его `getQueryData` не применяет),
 * поэтому читается `data.getRecipeById`.
 */
const canonicalPromotionRedirect = (
  urlSegment: string,
  data: unknown
): void => {
  const promotion = (
    data as
      | {
          pages?: Array<{
            promotion?: { id?: string | number; identify?: string | null };
          }>;
        }
      | undefined
  )?.pages?.[0]?.promotion;

  if (!promotion?.id) {
    return;
  }
  const canonical = promotion.identify
    ? `${promotion.identify}-${promotion.id}`
    : `${promotion.id}`;

  if (urlSegment === canonical) {
    return;
  }
  if (/^\d+$/.test(urlSegment)) {
    throw redirect({ to: `/promotions/${canonical}`, statusCode: 301 });
  }
  // Чужой slug — на список раздела, как в проде
  throw redirect({ to: '/promotions', statusCode: 301 });
};

export const Route = createFileRoute('/promotions/$id')({
  /**
   * ЭТАП 2 — честный 404 несуществующей акции: бэк (promotionsResolver)
   * кидает CONTENT_NOT_FOUND -> notFound() -> HTTP 404 + контент /404 без
   * редиректа. Как и у товара: лоадеры TanStack параллельны root-префетчу,
   * поэтому await ensureInfiniteQueryData (дедуп по pristine-ключу с
   * префетчем, лишнего запроса нет; серверный клиент с retry:false).
   * Прочие ошибки 404 не считаем; клиентскую навигацию не блокируем.
   */
  loader: async ({ params, location }) => {
    if (typeof window !== 'undefined') {
      return;
    }
    const urlData = parsePromotionUrlData(params.id);
    if (!urlData.id && !urlData.identify) {
      return;
    }
    const searchParams = new URLSearchParams(location.searchStr ?? '');
    const options = promotionByIdInfiniteQueryOptions(
      buildPromotionByIdKeyParams(urlData, searchParams.get('type'), null),
      Number(searchParams.get('page') || 0) || 1
    );
    const queryClient = getQueryClient();
    // Префетч успел упасть раньше — ошибка уже в кэше, без второго запроса
    const settled = queryClient.getQueryState(options.queryKey);
    if (settled?.error) {
      if (isContentNotFoundError(settled.error)) {
        throw notFound();
      }
      return;
    }
    try {
      await queryClient.ensureInfiniteQueryData(options);
    } catch (error) {
      if (isContentNotFoundError(error)) {
        throw notFound();
      }
      return;
    }
    canonicalPromotionRedirect(
      params.id,
      queryClient.getQueryData(options.queryKey)
    );
  },
  component: () => <CacheDataRoute component={Promotion} path='/promotions/:id' />
});
